import { useMemo } from 'react';
import { Permission } from '@weave/schema-gen-ts/dist/shared/waccess/acls.pb';
import { hasSchemaACL } from '@frontend/auth-helpers';
import { scopedFilterAndSort } from '@frontend/filters';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { usePhoneLocationFilterShallowStore, usePhoneSettingsShallowStore } from '@frontend/phone-config';
import { useSettingsNavigate } from '@frontend/settings-routing';
import { theme } from '@frontend/theme';
import { SwitchField, useFormField, Text, IconButton, useAlert } from '@frontend/design-system';
import { IndexTable } from '../../components/common/index-table';
import { PhoneSettingsPage } from '../../components/settings/settings-page';
import { CreateVoicemailBoxButton } from '../../components/voicemail-box/create-button';
import { DeleteVoicemailBoxIconButton } from '../../components/voicemail-box/delete-button';
import { VoicemailBoxReadResponse } from '../../components/voicemail-box/types';
import { useVoicemailBoxMutations } from '../../components/voicemail-box/use-voicemail-box-mutations';
import useVoicemailBoxes from '../../components/voicemail-box/use-voicemail-boxes';
import { trackingId } from '../../tracking';
import { logError } from '../../utils/loggers';
import { usePersistentPagination } from './usePersistentPagination';

export function VoicemailBoxIndex() {
  const { navigate } = useSettingsNavigate();
  const { locationFilters, setLocationFilters } = usePhoneLocationFilterShallowStore(
    'locationFilters',
    'setLocationFilters'
  );
  const { settingsTenantLocation, globalAvailableLocationIds } = usePhoneSettingsShallowStore(
    'settingsTenantLocation',
    'globalAvailableLocationIds'
  );
  const permissionBase = settingsTenantLocation?.id;
  const hasPhoneFullAccess = permissionBase ? hasSchemaACL(permissionBase, Permission.PHONE_FULL_ACCESS) : false;
  const { t } = useTranslation('phone');

  const filteredLocations = locationFilters.voicemailBoxesIndex;

  const { data: voicemailBoxes = [], isLoading } = useVoicemailBoxes({
    phoneTenantId: settingsTenantLocation?.phoneTenantId ?? '',
  });

  const filteredVoicemailBoxes = useMemo(
    () =>
      scopedFilterAndSort({
        data: voicemailBoxes,
        scopeIds: globalAvailableLocationIds,
        filteredScopeIds: filteredLocations,
        shouldSort: false,
      }),
    [voicemailBoxes, globalAvailableLocationIds, filteredLocations]
  );

  const [_pagination, _setPagination, paginationTableProps, paginationConfig] =
    usePersistentPagination('voicemail-boxes-pagination');

  return (
    <PhoneSettingsPage
      action={
        settingsTenantLocation ? (
          <CreateVoicemailBoxButton disabled={!hasPhoneFullAccess} tenantLocation={settingsTenantLocation} />
        ) : null
      }
      title={t('Voicemail Boxes')}
      subtitle={t('Click a voicemail box to edit the name, PIN, notifications and greetings.')}
      breadcrumbs={[
        { label: t('Phone'), to: '/phone/main' },
        { label: t('Voicemail Boxes'), to: '/phone/voicemail-box' },
      ]}
    >
      {settingsTenantLocation ? (
        <IndexTable
          {...paginationTableProps}
          labelsColumnPosition={2}
          tenantLocation={settingsTenantLocation}
          idAccessor={(data) => data.mailbox?.id}
          nameAccessor={(data) => data.mailbox?.name}
          labelsAccessor={(data) => data.locationIds ?? []}
          filteredLocations={filteredLocations}
          setFilteredLocations={(locations) => {
            setLocationFilters({ voicemailBoxesIndex: locations });
          }}
          DeleteIconButton={DeleteVoicemailBoxIconButton}
          canDeleteRow={(row) => row.isDeletable && hasPhoneFullAccess}
          tableConfig={{
            ...paginationConfig,
            isLoading,
            data: filteredVoicemailBoxes,
            uniqueRowId: (row) => row.mailbox?.id,
            colConfig: [
              {
                id: 'name',
                Header: t('Voicemail Box Name'),
                accessor: 'mailbox.name',
                sortType: 'string',
              },
              {
                id: 'extension',
                Header: t('Extension'),
                accessor: 'extension.number',
                cellRenderer: (value) => {
                  return value ?? '--';
                },
                width: 120,
                sortType: 'number',
              },
              {
                id: 'isShared',
                Header: () => (
                  <>
                    <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                      <Text size='medium' css={{ color: theme.colors.neutral40 }} as='span'>
                        {t('Share in App')}
                      </Text>
                      <span style={{ padding: theme.spacing(0.5) }}>
                        <IconButton
                          showLabelOnHover
                          size='xsmall'
                          css={{ padding: 0 }}
                          label={
                            settingsTenantLocation.locationType === 'unify'
                              ? t('Users in associated locations can access this Voicemail Box in Weave apps.')
                              : t('Users can access this Voicemail Box in Weave apps.')
                          }
                        >
                          <Icon name='info' size={16} color='subdued' />
                        </IconButton>
                      </span>
                    </div>
                  </>
                ),
                accessor: (row) => row,
                cellRenderer: (mailbox: VoicemailBoxReadResponse) => {
                  return mailbox ? (
                    <SharedSwitch
                      disabled={!hasPhoneFullAccess}
                      box={mailbox}
                      tenantId={settingsTenantLocation.phoneTenantId ?? ''}
                    />
                  ) : null;
                },
                width: 200,
                disableSortBy: true,
              },
            ],
          }}
          onRowClick={(row) => {
            navigate({ to: '/phone/voicemail-box/:id', params: { id: row.mailbox?.id } });
          }}
        />
      ) : (
        <div>{t('No Phone Tenant Selected')}</div>
      )}
    </PhoneSettingsPage>
  );
}

const SharedSwitch = ({
  box,
  tenantId,
  disabled = false,
}: {
  box: VoicemailBoxReadResponse;
  tenantId: string;
  disabled?: boolean;
}) => {
  const { update } = useVoicemailBoxMutations(tenantId);
  const switchField = useFormField({ type: 'switch', value: box.mailbox.sharedAccess }, [box.mailbox.sharedAccess]);
  const alerts = useAlert();
  return (
    <SwitchField
      {...switchField}
      disabled={disabled}
      onChange={(e) => {
        switchField.onChange(e);
        // We need to update the entire object just to update the shared access field
        update.mutate(
          {
            voicemailBoxId: box.mailbox.id,
            emailAddresses: box.mailbox.email ? box.mailbox.email.split(',') : [],
            extensionNumber: box.extension?.number,
            locationIds: box.locationIds ?? [],
            name: box.mailbox.name,
            pin: box.mailbox.pin,
            number: box.mailbox.number,
            playMessageDate: box.mailbox.playMessageDate ?? false,
            // @ts-ignore
            sharedAccess: e.value,
            sendNotification: box.mailbox.sendNotification ?? false,
            smsNumbers: box.mailbox.sms ? box.mailbox.sms.split(',') : [],
          },
          {
            onSuccess: () => {
              alerts.success('Shared access updated successfully.');
            },
            onError: () => {
              alerts.error('System failed to update shared access. Please try again.');
              logError('SharedSwitch:updateVoicemailBoxMutation | Failed to update shared access for voicemail box', {
                // @ts-ignore
                data: { voicemailBoxId: box.mailbox.id, tenantId, newSharedAccessValue: e.value },
              });
            },
          }
        );
      }}
      name={'shared-switch'}
      label=''
      trackingId={trackingId({ context: 'setting', feature: 'voicemail-boxes', details: 'index-shared-access' })}
    />
  );
};
